import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Stack,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { generateUUID } from "services/utils";
import { cloneDeep, isObject } from "lodash";

export const RootCauseCustomSection = (props) => {
  const { value, onChange, isLock, column } = props;

  const getState = (val) => {
    if (isObject(val) && val !== null && !Array.isArray(val)) {
      return {
        selectedMethod:
          val.selectedMethod ||
          (val.brainStormingSelected
            ? "Brain Storming"
            : val.whyWhySelected
            ? "Why-Why"
            : ""),
        brainStormingSelected: !!val.brainStormingSelected,
        brainStormingText: val.brainStormingText || "",
        whyWhySelected: !!val.whyWhySelected,
        whyWhyList:
          Array.isArray(val.whyWhyList) && val.whyWhyList.length > 0
            ? val.whyWhyList
            : [{ id: generateUUID(), why: "", answer: "" }],
      };
    }
    if (typeof val === "string" && val.trim() !== "") {
      return {
        selectedMethod: "Brain Storming",
        brainStormingSelected: true,
        brainStormingText: val,
        whyWhySelected: false,
        whyWhyList: [{ id: generateUUID(), why: "", answer: "" }],
      };
    }
    return {
      selectedMethod: "",
      brainStormingSelected: false,
      brainStormingText: "",
      whyWhySelected: false,
      whyWhyList: [{ id: generateUUID(), why: "", answer: "" }],
    };
  };

  const [state, setState] = useState(() => getState(value));

  useEffect(() => {
    if (!value || typeof value === "string") {
      onChange && onChange(state);
    }
  }, []);

  const updateAndNotify = (updater) => {
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      onChange && onChange(next);
      return next;
    });
  };

  const handleBrainStormingTextChange = (e) => {
    updateAndNotify((prev) => ({
      ...prev,
      brainStormingText: e.target.value,
    }));
  };

  const handleWhyChange = (index, val) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev.whyWhyList);
      if (newList[index]) {
        newList[index].why = val;
      }
      return { ...prev, whyWhyList: newList };
    });
  };

  const handleAnswerChange = (index, val) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev.whyWhyList);
      if (newList[index]) {
        newList[index].answer = val;
      }
      return { ...prev, whyWhyList: newList };
    });
  };

  const handleAddWhyRow = () => {
    updateAndNotify((prev) => ({
      ...prev,
      whyWhyList: [
        ...prev.whyWhyList,
        { id: generateUUID(), why: "", answer: "" },
      ],
    }));
  };

  const handleDeleteWhyRow = (index) => {
    updateAndNotify((prev) => {
      const newList = prev.whyWhyList.filter((_, i) => i !== index);
      return {
        ...prev,
        whyWhyList:
          newList.length > 0
            ? newList
            : [{ id: generateUUID(), why: "", answer: "" }],
      };
    });
  };

  const hasOptions =
    Array.isArray(column?.options) && column?.options.length > 0;

  return (
    <Box
      sx={{
        border: "1px solid #c4c4c4",
        borderRadius: "6px",
        p: 2,
        mb: 2,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: "#eaeaea",
          p: 1.5,
          borderRadius: "4px",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#222",
            whiteSpace: "nowrap",
          }}
        >
          {column?.name || "Root Cause Statement"}
        </Typography>

        <Box sx={{ width: 250 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Select Method"
            InputLabelProps={{ shrink: true }}
            value={
              state.selectedMethod
                ? state.selectedMethod
                : state.brainStormingSelected
                ? "Brain Storming"
                : state.whyWhySelected
                ? "Why-Why"
                : ""
            }
            onChange={(e) => {
              const val = e.target.value;
              const normalized = val.toLowerCase().replace(/[\s-]/g, "");
              const isBrain =
                normalized.includes("brainstorming") ||
                val === "Brain Storming";
              const isWhy = normalized.includes("whywhy") || val === "Why-Why";

              updateAndNotify((prev) => ({
                ...prev,
                selectedMethod: val,
                brainStormingSelected: isBrain,
                whyWhySelected: isWhy,
              }));
            }}
            disabled={isLock}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected || selected === "") {
                  return (
                    <span style={{ color: "#9e9e9e" }}>Select method...</span>
                  );
                }
                return selected;
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
              },
            }}
          >
            <MenuItem value="">
              <em>Select method...</em>
            </MenuItem>
            {hasOptions
              ? column.options.map((opt, idx) => {
                  const optString =
                    typeof opt === "string"
                      ? opt
                      : opt?.name || opt?.label || JSON.stringify(opt);
                  return (
                    <MenuItem key={idx} value={optString}>
                      {optString}
                    </MenuItem>
                  );
                })
              : [
                  <MenuItem key="bs" value="Brain Storming">
                    Brain Storming
                  </MenuItem>,
                  <MenuItem key="ww" value="Why-Why">
                    Why-Why
                  </MenuItem>,
                ]}
          </TextField>
        </Box>
      </Stack>

      {state.brainStormingSelected && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Brain Storming
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Write Brain Storming analysis here..."
            value={state.brainStormingText}
            onChange={handleBrainStormingTextChange}
            disabled={isLock}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
              },
            }}
          />
        </Box>
      )}

      {state.whyWhySelected && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Why-Why Analysis
          </Typography>

          {state.whyWhyList.map((item, idx) => (
            <Box
              key={item.id || idx}
              sx={{
                p: 1.5,
                mb: 1.5,
                border: "1px solid #e8e8e8",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ position: "relative" }}>
                {!isLock && state.whyWhyList.length > 1 && (
                  <Box
                    sx={{ position: "absolute", top: 0, right: 0, zIndex: 1 }}
                  >
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteWhyRow(idx)}
                      title="Delete Why/Answer Row"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Stack
                  spacing={1.5}
                  sx={{ pr: !isLock && state.whyWhyList.length > 1 ? 4 : 0 }}
                >
                  <Stack spacing={0.5}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "#555" }}
                    >
                      Why {idx + 1}:
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`Enter question / why ${idx + 1}...`}
                      value={item.why || ""}
                      onChange={(e) => handleWhyChange(idx, e.target.value)}
                      disabled={isLock}
                    />
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "#555" }}
                    >
                      Justification {idx + 1}:
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`Enter answer ${idx + 1}...`}
                      value={item.answer || ""}
                      onChange={(e) => handleAnswerChange(idx, e.target.value)}
                      disabled={isLock}
                    />
                  </Stack>
                </Stack>
              </Box>
            </Box>
          ))}

          {!isLock && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={handleAddWhyRow}
              sx={{ mt: 0.5, textTransform: "none", fontWeight: 500 }}
            >
              Add two rows on (+)
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

const defaultRowItem = (overrides = {}) => ({
  id: generateUUID(),
  actionTypes: [],
  assignedTo: [],
  assignedToId: [],
  _id: [],
  targetDate: "",
  status: "NA",
  isAutoGenerated: false,
  parentRowId: null,
  docRef: "",
  documentChange: {
    selected: false,
    documentNameNumber: [],
  },
  training: {
    selected: false,
    trainingTopic: "",
  },
  other: {
    selected: false,
    description: "",
  },
  ...overrides,
});

const defaultSectionItem = (overrides = {}) => {
  const initialRow = defaultRowItem();
  return {
    id: generateUUID(),
    statement: "",
    rows: [initialRow],
    ...overrides,
  };
};

const formatDocOption = (opt) => {
  if (!opt) return null;
  if (typeof opt === "string") {
    return {
      label: opt,
      documentNameNumber: opt,
      documentName: opt,
      documentNumber: "",
      documentId: "",
      _id: "",
    };
  }
  const docNumber =
    opt.documentNumber ||
    opt.formatNumber ||
    opt.number ||
    opt.qualityNumber ||
    "";
  const docName =
    opt.documentName || opt.name || opt.defaultName || opt.title || "";
  const label =
    opt.label ||
    opt.documentNameNumber ||
    `${docNumber} - ${docName}`.replace(/^ - | - $/g, "").trim() ||
    "Untitled Document";
  return {
    ...opt,
    label,
    documentNameNumber: label,
    documentName: docName || label,
    documentNumber: docNumber,
    documentId: opt.documentId || opt._id || opt.id || "",
    _id: opt._id || opt.documentId || opt.id || "",
  };
};

const syncRowsForSection = (rows = []) => {
  const sourceRows =
    Array.isArray(rows) && rows.length > 0 ? rows : [defaultRowItem()];

  const nonAutoRows = sourceRows.filter((r) => !r.isAutoGenerated);
  const autoRowsPool = sourceRows.filter((r) => r.isAutoGenerated);

  const result = [];

  for (const parentRow of nonAutoRows) {
    result.push(parentRow);

    const mainAction =
      Array.isArray(parentRow.actionTypes) && parentRow.actionTypes.length > 0
        ? parentRow.actionTypes[0]
        : parentRow.actionTypes;

    if (mainAction === "Document Change") {
      const docs = Array.isArray(parentRow.documentChange?.documentNameNumber)
        ? parentRow.documentChange.documentNameNumber
        : parentRow.documentChange?.documentNameNumber
        ? [parentRow.documentChange.documentNameNumber]
        : [];

      const cleanDocs = docs
        .map((d) => {
          if (!d) return null;
          if (typeof d === "string") {
            const trimmed = d.trim();
            return trimmed
              ? { docName: trimmed, docObj: formatDocOption(trimmed) }
              : null;
          }
          if (typeof d === "object") {
            const formatted = formatDocOption(d);
            const docName =
              formatted?.label || formatted?.documentNameNumber || "";
            return docName ? { docName, docObj: formatted } : null;
          }
          return null;
        })
        .filter(Boolean);

      for (const { docName, docObj } of cleanDocs) {
        let matchedIndex = autoRowsPool.findIndex(
          (r) =>
            (r.parentRowId === parentRow.id &&
              (r.docRef === docName ||
                (docObj?.documentId && r.documentId === docObj.documentId) ||
                (docObj?._id && r._id === docObj._id))) ||
            (r.actionTypes?.[0] === "Training" &&
              (r.training?.trainingTopic === `Training on revise ${docName}` ||
                (docObj?.documentId && r.documentId === docObj.documentId))),
        );

        if (matchedIndex !== -1) {
          const matchedRow = autoRowsPool[matchedIndex];
          autoRowsPool.splice(matchedIndex, 1);
          result.push({
            ...matchedRow,
            isAutoGenerated: true,
            parentRowId: parentRow.id,
            docRef: docName,
            documentId: docObj?.documentId || docObj?._id || docObj?.id || null,
            _id: docObj?._id || docObj?.documentId || docObj?.id || null,
            docObject: docObj,
            actionTypes: ["Training"],
            status:
              matchedRow.status === "NA" ? "Open" : matchedRow.status || "Open",
            training: {
              ...matchedRow.training,
              selected: true,
              trainingTopic: `Training on revise ${docName}`,
            },
            documentChange: {
              ...matchedRow.documentChange,
              selected: false,
              documentNameNumber: [],
            },
            other: {
              ...matchedRow.other,
              selected: false,
              description: "",
            },
          });
        } else {
          result.push(
            defaultRowItem({
              actionTypes: ["Training"],
              isAutoGenerated: true,
              status: "Open",
              parentRowId: parentRow.id,
              docRef: docName,
              documentId:
                docObj?.documentId || docObj?._id || docObj?.id || null,
              _id: docObj?._id || docObj?.documentId || docObj?.id || null,
              docObject: docObj,
              training: {
                selected: true,
                trainingTopic: `Training on revise ${docName}`,
              },
            }),
          );
        }
      }
    }
  }
  return result.length > 0 ? result : [defaultRowItem()];
};

export const checkRowsClosed = (list) => {
  if (!Array.isArray(list) || list.length === 0) return true;

  const isRowActive = (row) =>
    (Array.isArray(row?.actionTypes) && row?.actionTypes?.length > 0) ||
    (typeof row?.actionType === "string" && row?.actionType?.trim() !== "") ||
    row?.documentChange?.selected === true ||
    row?.training?.selected === true ||
    row?.other?.selected === true;

  const activeItems = list.filter(
    (item) => Array.isArray(item?.rows) && item.rows.some(isRowActive),
  );

  if (activeItems.length === 0) return true;

  return activeItems.every((item) => {
    const validRows = item.rows.filter(isRowActive);
    return validRows.every((row) => {
      const childRows = item.rows.filter(
        (r) =>
          (r?.parentRowId && r?.parentRowId === row?.id) ||
          (r?.parentRowId && r?.parentRowId === row?._id),
      );
      if (childRows.length > 0) {
        return childRows.every((child) => {
          const status =
            child?.changeNoteId?.currentStatus ||
            child?.changeNoteStatus ||
            child?.status;
          return (
            status?.toLowerCase() === "closed" ||
            status?.toLowerCase() === "close"
          );
        });
      }
      const status =
        row?.changeNoteId?.currentStatus ||
        row?.changeNoteStatus ||
        row?.status;
      return (
        status?.toLowerCase() === "closed" || status?.toLowerCase() === "close"
      );
    });
  });
};

export const CorrectionCustomSection = (props) => {
  const { value, onChange, isLock, column, config } = props;

  const rawSubColumns =
    Array.isArray(column?.columns) && column?.columns.length > 0
      ? column?.columns
      : Array.isArray(column?.value) && column?.value.length > 0
      ? column?.value
      : Array.isArray(column?.val) && column?.val.length > 0
      ? column?.val
      : [
          { name: "Action Type", width: "20%" },
          { name: "Details", width: "30%" },
          { name: "Assigned To", width: "20%" },
          { name: "Target Date", width: "18%" },
          { name: "Status", width: "12%" },
        ];

  const subColumns = (() => {
    const cols = cloneDeep(rawSubColumns);
    const hasDetails = cols.some(
      (c) => (c?.name || c?.n || c?.uniqueName || c?.u) === "Details",
    );
    if (!hasDetails) {
      const idx = cols.findIndex(
        (c) => (c?.name || c?.n || c?.uniqueName || c?.u) === "Action Type",
      );
      const detailsCol = { name: "Details", n: "Details", width: "30%" };
      if (idx !== -1) {
        cols.splice(idx + 1, 0, detailsCol);
      } else {
        cols.splice(1, 0, detailsCol);
      }
    }
    return cols;
  })();

  const getState = (val) => {
    if (Array.isArray(val) && val.length > 0) {
      return val.map((item) => {
        const rawRows =
          Array.isArray(item.rows) && item.rows.length > 0
            ? item.rows
            : Array.isArray(item.actions) && item.actions.length > 0
            ? item.actions
            : null;

        let initialRows;
        if (rawRows) {
          initialRows = rawRows.map((r) => ({
            ...defaultRowItem(),
            ...r,
            actionTypes: Array.isArray(r.actionTypes)
              ? r.actionTypes
              : r.actionType
              ? [r.actionType]
              : [],
            documentChange: {
              ...defaultRowItem().documentChange,
              ...(r.documentChange || {}),
              documentNameNumber: Array.isArray(
                r.documentChange?.documentNameNumber,
              )
                ? r.documentChange.documentNameNumber
                : r.documentChange?.documentNameNumber
                ? [r.documentChange.documentNameNumber]
                : [],
            },
            training: {
              ...defaultRowItem().training,
              ...(r.training || {}),
            },
            other: { ...defaultRowItem().other, ...(r.other || {}) },
          }));
        } else {
          initialRows = [
            {
              ...defaultRowItem(),
              ...item,
              id: item.rowId || generateUUID(),
              actionTypes: Array.isArray(item.actionTypes)
                ? item.actionTypes
                : item.actionType
                ? [item.actionType]
                : [],
              documentChange: {
                ...defaultRowItem().documentChange,
                ...(item.documentChange || {}),
                documentNameNumber: Array.isArray(
                  item.documentChange?.documentNameNumber,
                )
                  ? item.documentChange.documentNameNumber
                  : item.documentChange?.documentNameNumber
                  ? [item.documentChange.documentNameNumber]
                  : [],
              },
              training: {
                ...defaultRowItem().training,
                ...(item.training || {}),
              },
              other: { ...defaultRowItem().other, ...(item.other || {}) },
            },
          ];
        }

        const synched = syncRowsForSection(initialRows);
        const { actions, ...rest } = item;
        return {
          ...rest,
          id: item.id || generateUUID(),
          statement: item.statement || "",
          rows: synched,
        };
      });
    }
    if (typeof val === "string" && val.trim() !== "") {
      return [defaultSectionItem({ statement: val })];
    }
    return [defaultSectionItem()];
  };

  const [sectionsList, setSectionsList] = useState(() => getState(value));

  useEffect(() => {
    if (!value || typeof value === "string" || !Array.isArray(value)) {
      onChange && onChange(sectionsList);
    }
  }, []);

  const updateAndNotify = (updater) => {
    setSectionsList((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const synchronizedNext = next.map((sec) => {
        const { actions, ...rest } = sec;
        const synchedRows = syncRowsForSection(sec.rows || sec.actions || []);
        return {
          ...rest,
          rows: synchedRows,
        };
      });
      onChange && onChange(synchronizedNext);
      return synchronizedNext;
    });
  };

  const handleAddSection = () => {
    updateAndNotify((prev) => [...prev, defaultSectionItem()]);
  };

  const handleDeleteSection = (index) => {
    updateAndNotify((prev) => {
      const newList = prev.filter((_, i) => i !== index);
      return newList.length > 0 ? newList : [defaultSectionItem()];
    });
  };

  const handleSectionFieldChange = (index, field, val) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev);
      if (newList[index]) {
        newList[index][field] = val;
      }
      return newList;
    });
  };

  const handleAddRow = (secIdx) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev);
      const sec = newList[secIdx];
      if (sec) {
        sec.rows = Array.isArray(sec.rows)
          ? [...sec.rows, defaultRowItem()]
          : [defaultRowItem()];
      }
      return newList;
    });
  };

  const handleDeleteRow = (secIdx, rowIdx) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev);
      const sec = newList[secIdx];
      if (sec && Array.isArray(sec.rows)) {
        const remaining = sec.rows.filter((_, i) => i !== rowIdx);
        sec.rows = remaining.length > 0 ? remaining : [defaultRowItem()];
      }
      return newList;
    });
  };

  const handleRowActionTypeChange = (secIdx, rowIdx, val) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev);
      const sec = newList[secIdx];
      if (sec && sec.rows && sec.rows[rowIdx]) {
        const row = sec.rows[rowIdx];
        row.actionTypes = val ? [val] : [];
        row.documentChange.selected = val === "Document Change";
        row.training.selected = val === "Training";
        row.other.selected = val === "Other";
        if (val !== "Document Change") {
          row.documentChange.documentNameNumber = [];
          row.documentChange.documentId = [];
          row.documentChange._id = [];
        }

        if (
          row.documentChange.selected ||
          row.training.selected ||
          row.other.selected
        ) {
          row.status = "Open";
        } else {
          row.status = "NA";
        }
      }
      return newList;
    });
  };

  const handleRowSubFieldChange = (secIdx, rowIdx, subKey, field, val) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev);
      const sec = newList[secIdx];
      if (sec && sec.rows && sec.rows[rowIdx] && sec.rows[rowIdx][subKey]) {
        sec.rows[rowIdx][subKey][field] = val;
        if (subKey === "documentChange" && field === "documentNameNumber") {
          const docsArr = Array.isArray(val) ? val : [val].filter(Boolean);
          const ids = docsArr
            .map((item) =>
              typeof item === "object"
                ? item.documentId || item._id || item.id || null
                : null,
            )
            .filter(Boolean);
          const _ids = docsArr
            .map((item) =>
              typeof item === "object"
                ? item._id || item.documentId || item.id || null
                : null,
            )
            .filter(Boolean);
          if (ids.length > 0) sec.rows[rowIdx][subKey].documentId = ids;
          if (_ids.length > 0) sec.rows[rowIdx][subKey]._id = _ids;
        }
      }
      return newList;
    });
  };

  const handleRowFieldChange = (secIdx, rowIdx, field, val) => {
    updateAndNotify((prev) => {
      const newList = cloneDeep(prev);
      const sec = newList[secIdx];
      if (sec && sec.rows && sec.rows[rowIdx]) {
        sec.rows[rowIdx][field] = val;
        if (field === "assignedTo") {
          const usersArr = Array.isArray(val)
            ? val
            : (typeof val === "string"
                ? val
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [val]
              ).filter(Boolean);
          const ids = usersArr
            .map((u) =>
              typeof u === "object" ? u.userId || u._id || u.id || null : null,
            )
            .filter(Boolean);
          const _ids = usersArr
            .map((u) =>
              typeof u === "object" ? u._id || u.userId || u.id || null : null,
            )
            .filter(Boolean);
          if (ids.length > 0) sec.rows[rowIdx].assignedToId = ids;
          if (_ids.length > 0) sec.rows[rowIdx]._id = _ids;
        }
      }
      return newList;
    });
  };

  const isCorrectiveAction =
    column?.uniqueName === "correctiveAction" ||
    column?.name?.toLowerCase()?.includes("corrective action");

  const isSectionClosed = checkRowsClosed(sectionsList);

  const isAnyActionSelected = sectionsList?.some((section) =>
    (section?.rows || [])?.some(
      (row) => row?.actionTypes && row?.actionTypes?.length > 0,
    ),
  );

  return (
    <Box
      sx={{
        border: "1px solid #c4c4c4",
        borderRadius: "6px",
        p: 2,
        mb: 2,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        width: "100%",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          mb: 1,
          color: "#222",
          borderBottom: "1px solid #e0e0e0",
          pb: 0.75,
        }}
      >
        {column?.name ||
          (isCorrectiveAction ? "Corrective action" : "Correction")}
        :
      </Typography>

      <Typography
        variant="body2"
        sx={{ fontWeight: 500, mb: 2, color: "#555" }}
      >
        Status:{" "}
        {!isAnyActionSelected ? (
          <span style={{ fontWeight: 600, color: "#555" }}>NA</span>
        ) : isSectionClosed ? (
          <span style={{ fontWeight: 600, color: "#2e7d32" }}>Closed</span>
        ) : (
          <span style={{ fontWeight: 600, color: "#1976d2" }}>Open</span>
        )}
      </Typography>

      {sectionsList.map((section, secIdx) => (
        <Paper
          key={section.id || secIdx}
          variant="outlined"
          sx={{
            p: 2,
            mb: 2.5,
            borderColor: "#d5d5d5",
            backgroundColor: "#fcfcfc",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              backgroundColor: "#eaeaea",
              p: 1.5,
              borderRadius: "4px",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#333" }}
            >
              {isCorrectiveAction
                ? `Corrective Action Statement ${secIdx + 1}`
                : `Correction ${secIdx + 1}`}
            </Typography>

            <Stack direction="row" spacing={1}>
              {!isLock && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddSection}
                  sx={{
                    backgroundColor: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  + option to add section
                </Button>
              )}
              {!isLock && sectionsList.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteSection(secIdx)}
                  title="Delete Section"
                  sx={{ backgroundColor: "#fff" }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder={`Enter statement / description for ${
              isCorrectiveAction ? "Corrective Action" : "Correction"
            } ${secIdx + 1}...`}
            value={section.statement || ""}
            onChange={(e) =>
              handleSectionFieldChange(secIdx, "statement", e.target.value)
            }
            disabled={isLock}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
              },
            }}
          />

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 2, borderColor: "#e0e0e0" }}
          >
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
                <TableRow>
                  {subColumns.map((subCol, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        fontWeight: 700,
                        width: subCol?.width || `${100 / subColumns.length}%`,
                      }}
                    >
                      {subCol?.name ||
                        subCol?.n ||
                        subCol?.uniqueName ||
                        subCol?.u}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(section.rows || []).map((row, rowIdx) => {
                  const mainAction =
                    Array.isArray(row.actionTypes) && row.actionTypes.length > 0
                      ? row.actionTypes[0]
                      : row.actionTypes || "";

                  return (
                    <TableRow
                      key={row.id || rowIdx}
                      sx={{ "& td": { pt: 1.5 } }}
                    >
                      <TableCell>
                        <Stack spacing={1}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={mainAction}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleRowActionTypeChange(secIdx, rowIdx, val);
                            }}
                            disabled={isLock || row.isAutoGenerated}
                            SelectProps={{
                              displayEmpty: true,
                              renderValue: (selected) => {
                                if (!selected || selected === "") {
                                  return (
                                    <span style={{ color: "#9e9e9e" }}>
                                      Select...
                                    </span>
                                  );
                                }
                                return selected;
                              },
                            }}
                            sx={{
                              backgroundColor:
                                isLock || row.isAutoGenerated
                                  ? "#f5f5f5"
                                  : "#fff",
                            }}
                          >
                            <MenuItem value="">
                              <em>Select...</em>
                            </MenuItem>
                            <MenuItem value="Document Change">
                              Document Change
                            </MenuItem>
                            <MenuItem value="Training">Training</MenuItem>
                            {/* <MenuItem value="Other">Other</MenuItem> */}
                          </TextField>
                          {row?.changeNoteId?.recordNumber && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1.5,
                                py: 0.75,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                bgcolor: "grey.50",
                                mt: 1,
                                maxWidth: "fit-content",
                              }}
                            >
                              <Typography
                                variant="caption"
                                onClick={() => {
                                  if (
                                    row?.changeNoteStatus?.toLowerCase() ===
                                    "open"
                                  ) {
                                    window.open(
                                      `/change-note/manage/${row?.changeNoteId?._id}`,
                                      "_blank",
                                    );
                                  } else {
                                    window.open(
                                      `/change-note/view/${row?.changeNoteId?._id}`,
                                      "_blank",
                                    );
                                  }
                                }}
                                sx={{
                                  color: "primary.main",
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  fontWeight: 600,
                                  "&:hover": {
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                Change Note No:{" "}
                                {row?.changeNoteId?.recordNumber}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          if (mainAction === "Document Change") {
                            const hasOptions =
                              Array.isArray(column?.options) &&
                              column?.options.length > 0;

                            const optionsList = hasOptions
                              ? column.options
                                  .map(formatDocOption)
                                  .filter(Boolean)
                              : [];

                            const rawCurrentDocs = Array.isArray(
                              row.documentChange?.documentNameNumber,
                            )
                              ? row.documentChange.documentNameNumber
                              : row.documentChange?.documentNameNumber
                              ? [row.documentChange.documentNameNumber]
                              : [];

                            const currentDocs = rawCurrentDocs
                              .map((doc) => {
                                if (typeof doc === "string") {
                                  const matched = optionsList.find(
                                    (opt) =>
                                      opt.label === doc ||
                                      opt.documentNameNumber === doc ||
                                      opt.documentName === doc ||
                                      `${opt.documentNumber} - ${opt.documentName}` ===
                                        doc,
                                  );
                                  return matched || formatDocOption(doc);
                                }
                                return formatDocOption(doc);
                              })
                              .filter(Boolean);

                            return (
                              <Autocomplete
                                fullWidth
                                size="small"
                                multiple
                                freeSolo={!hasOptions}
                                disabled={isLock || row.isAutoGenerated}
                                options={optionsList}
                                value={currentDocs}
                                getOptionLabel={(option) => {
                                  if (typeof option === "string") return option;
                                  return (
                                    option?.label ||
                                    option?.documentNameNumber ||
                                    option?.documentName ||
                                    ""
                                  );
                                }}
                                isOptionEqualToValue={(option, val) => {
                                  if (!option || !val) return false;
                                  if (
                                    option.documentId &&
                                    val.documentId &&
                                    option.documentId === val.documentId
                                  ) {
                                    return true;
                                  }
                                  if (
                                    option._id &&
                                    val._id &&
                                    option._id === val._id
                                  ) {
                                    return true;
                                  }
                                  const optLabel =
                                    typeof option === "string"
                                      ? option
                                      : option.label ||
                                        option.documentNameNumber ||
                                        "";
                                  const valLabel =
                                    typeof val === "string"
                                      ? val
                                      : val.label ||
                                        val.documentNameNumber ||
                                        "";
                                  return optLabel === valLabel;
                                }}
                                onChange={(event, newValue) => {
                                  const selectedObjects = (newValue || [])
                                    .map((item) => {
                                      if (typeof item === "string") {
                                        const matched = optionsList.find(
                                          (opt) =>
                                            opt.label === item ||
                                            opt.documentNameNumber === item ||
                                            opt.documentName === item,
                                        );
                                        return matched || formatDocOption(item);
                                      }
                                      return formatDocOption(item);
                                    })
                                    .filter(Boolean);

                                  handleRowSubFieldChange(
                                    secIdx,
                                    rowIdx,
                                    "documentChange",
                                    "documentNameNumber",
                                    selectedObjects,
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Document..."
                                    sx={{
                                      backgroundColor:
                                        isLock || row.isAutoGenerated
                                          ? "#f5f5f5"
                                          : "#fff",
                                    }}
                                  />
                                )}
                              />
                            );
                          }
                          if (mainAction === "Training") {
                            return (
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Training Topic..."
                                value={row.training?.trainingTopic || ""}
                                onChange={(e) =>
                                  handleRowSubFieldChange(
                                    secIdx,
                                    rowIdx,
                                    "training",
                                    "trainingTopic",
                                    e.target.value,
                                  )
                                }
                                disabled={isLock || row.isAutoGenerated}
                                sx={{
                                  backgroundColor:
                                    isLock || row.isAutoGenerated
                                      ? "#f5f5f5"
                                      : "#fff",
                                }}
                              />
                            );
                          }
                          if (mainAction === "Other") {
                            return (
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Description..."
                                value={row.other?.description || ""}
                                onChange={(e) =>
                                  handleRowSubFieldChange(
                                    secIdx,
                                    rowIdx,
                                    "other",
                                    "description",
                                    e.target.value,
                                  )
                                }
                                disabled={isLock || row.isAutoGenerated}
                                sx={{
                                  backgroundColor:
                                    isLock || row.isAutoGenerated
                                      ? "#f5f5f5"
                                      : "#fff",
                                }}
                              />
                            );
                          }
                          return (
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Select Action Type first..."
                              disabled={true}
                              sx={{ backgroundColor: "#f5f5f5" }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {props?.config?.dynamicOptions?.allUsers &&
                        props.config.dynamicOptions.allUsers.length > 0 ? (
                          <Autocomplete
                            fullWidth
                            size="small"
                            multiple
                            disabled={isLock}
                            options={props.config.dynamicOptions.allUsers}
                            groupBy={(option) => option.department || "Other"}
                            getOptionLabel={(option) => {
                              if (typeof option === "string") return option;
                              return (
                                option.userDesignation || option.name || ""
                              );
                            }}
                            value={(() => {
                              const rawAssigned = Array.isArray(row?.assignedTo)
                                ? row.assignedTo
                                : (row?.assignedTo || "")
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean);
                              return rawAssigned
                                .map((item) => {
                                  if (typeof item === "string") {
                                    return (
                                      props.config.dynamicOptions.allUsers.find(
                                        (u) =>
                                          u?.userDesignation === item ||
                                          u?.name === item ||
                                          u?.userId === item ||
                                          u?._id === item,
                                      ) || {
                                        userDesignation: item,
                                        name: item,
                                        userId: "",
                                        _id: "",
                                      }
                                    );
                                  }
                                  return item;
                                })
                                .filter(Boolean);
                            })()}
                            isOptionEqualToValue={(option, val) => {
                              if (!option || !val) return false;
                              if (
                                option.userId &&
                                val.userId &&
                                option.userId === val.userId
                              ) {
                                return true;
                              }
                              if (
                                option._id &&
                                val._id &&
                                option._id === val._id
                              ) {
                                return true;
                              }
                              const optLabel =
                                typeof option === "string"
                                  ? option
                                  : option.userDesignation || option.name || "";
                              const valLabel =
                                typeof val === "string"
                                  ? val
                                  : val.userDesignation || val.name || "";
                              return optLabel === valLabel;
                            }}
                            onChange={(event, newValue) => {
                              const selectedUsers = newValue || [];
                              handleRowFieldChange(
                                secIdx,
                                rowIdx,
                                "assignedTo",
                                selectedUsers,
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Assigned to"
                                sx={{
                                  backgroundColor: isLock ? "#f5f5f5" : "#fff",
                                }}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Assigned to"
                            value={
                              Array.isArray(row.assignedTo)
                                ? row.assignedTo
                                    .map((u) =>
                                      typeof u === "object"
                                        ? u.userDesignation || u.name || ""
                                        : u,
                                    )
                                    .join(", ")
                                : row.assignedTo || ""
                            }
                            onChange={(e) =>
                              handleRowFieldChange(
                                secIdx,
                                rowIdx,
                                "assignedTo",
                                e.target.value,
                              )
                            }
                            disabled={isLock}
                            sx={{
                              backgroundColor: isLock ? "#f5f5f5" : "#fff",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          fullWidth
                          size="small"
                          value={row.targetDate || ""}
                          onChange={(e) =>
                            handleRowFieldChange(
                              secIdx,
                              rowIdx,
                              "targetDate",
                              e.target.value,
                            )
                          }
                          disabled={isLock}
                          InputLabelProps={{ shrink: true }}
                          sx={{ backgroundColor: isLock ? "#f5f5f5" : "#fff" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {/* <TextField
                            fullWidth
                            size="small"
                            value={row?.changeNoteStatus || row?.status || ""}
                            disabled={true}
                            sx={{ backgroundColor: "#f5f5f5" }}
                          /> */}
                          <Typography>
                            {row?.changeNoteStatus || row?.status || ""}
                          </Typography>
                          {!isLock &&
                            (section.rows || []).length > 1 &&
                            !row.isAutoGenerated && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteRow(secIdx, rowIdx)}
                                title="Delete Action Row"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {!isLock && (
            <Box sx={{ mt: 1, mb: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() => handleAddRow(secIdx)}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  backgroundColor: "#fff",
                }}
              >
                + Add Action Row
              </Button>
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );
};

const defaultStaticColumns = [
  {
    name: "Raised By",
    value: "raisedBy",
    defaultName: "raisedBy",
    type: "dropdown",
    renderOptionValue: "userDesignation",
    options: [],
    isReadOnly: true,
    uniqueName: "raisedBy",
    isDelete: false,
  },
  {
    name: "Complaint No",
    value: "complaintNo",
    isReadOnly: true,
    uniqueName: "complaintNo",
    type: "text",
    isDelete: false,
  },
  {
    name: "Description Of Complaint",
    value: "descriptionOfComplaint",
    type: "textarea",
    validation: { required: true, msg: "Description Of Complaint is required" },
    uniqueName: "descriptionOfComplaint",
    isReadOnly: false,
    isDelete: false,
  },
  {
    name: "Root Cause Statement",
    value: "rootCauseStatement",
    type: "textarea",
    validation: { required: true, msg: "Root Cause Statement is required" },
    uniqueName: "rootCauseStatement",
    isReadOnly: false,
    isDelete: false,
  },
  {
    name: "Correction",
    value: "correction",
    type: "textarea",
    validation: { required: true, msg: "Correction is required" },
    uniqueName: "correction",
    isReadOnly: false,
    isDelete: false,
  },
  {
    name: "Corrective Action",
    value: "correctiveAction",
    type: "textarea",
    validation: { required: true, msg: "Corrective Action is required" },
    uniqueName: "correctiveAction",
    isReadOnly: false,
    isDelete: false,
  },
  {
    name: "Date of verification of effectiveness of NC closure",
    value: "dateOfVerificationOfEffectivenessOfNCClosure",
    type: "date",
    validation: { required: true, msg: "Date is required" },
    uniqueName: "dateOfVerificationOfEffectivenessOfNCClosure",
    isReadOnly: false,
    isDelete: false,
  },
  {
    name: "Reviewed By",
    value: "reviewedBy",
    type: "dropdown",
    renderOptionValue: "userDesignation",
    isReadOnly: true,
    uniqueName: "reviewedBy",
    isDelete: false,
  },
  {
    name: "Approved By",
    value: "approvedBy",
    type: "dropdown",
    renderOptionValue: "userDesignation",
    isReadOnly: true,
    uniqueName: "approvedBy",
    isDelete: false,
  },
  {
    name: "Checked By",
    value: "checkedBy",
    type: "esign",
    isReadOnly: true,
    uniqueName: "checkedBy",
    isDelete: false,
  },
];

export const enrichCorrectiveActionColumns = (columns = []) => {
  const sourceColumns =
    Array.isArray(columns) && columns.length > 0
      ? columns
      : defaultStaticColumns;

  const updated = sourceColumns.map((col) => {
    if (col?.uniqueName === "rootCauseStatement") {
      return {
        ...col,
        type: "custom",
        isFullWidth: true,
        customComponent: RootCauseCustomSection,
      };
    }
    if (
      col?.uniqueName === "correction" ||
      col?.uniqueName === "correctiveAction"
    ) {
      return {
        ...col,
        type: "custom",
        isFullWidth: true,
        customComponent: CorrectionCustomSection,
      };
    }
    return col;
  });

  return updated;
};
